import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import API from '@/lib/api';
import { MagnifyingGlass, ArrowRight, Receipt, Tray, XCircle, Hash, Tag } from '@phosphor-icons/react';

export default function ZoekReferentiePage() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ uitgaand: [], inkomend: [] });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const timer = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 2) {
      setResults({ uitgaand: [], inkomend: [] });
      setSearched(false);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await API.get(`/zoek-referentie?q=${encodeURIComponent(q.trim())}`);
        setResults(r.data || { uitgaand: [], inkomend: [] });
        setSearched(true);
      } catch { setResults({ uitgaand: [], inkomend: [] }); }
      setLoading(false);
    }, 300);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q]);

  const fmtEur = (n) => (Number(n) || 0).toLocaleString('nl-BE', { style: 'currency', currency: 'EUR' });
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

  // Highlight match in text
  const highlight = (text) => {
    if (!q || !text) return text;
    const parts = String(text).split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((p, i) => p.toLowerCase() === q.toLowerCase() ? <mark key={i} className="bg-yellow-200 text-gray-900 font-bold px-0.5">{p}</mark> : <span key={i}>{p}</span>);
  };

  const totalResults = results.uitgaand.length + results.inkomend.length;

  return (
    <div data-testid="zoek-ref-page">
      <div className="mb-5">
        <h1 className="page-heading mb-1 flex items-center gap-2"><Hash size={22} weight="bold" /> Zoeken op referentie</h1>
        <p className="text-sm text-gray-500">Zoek werkbonnummers, ordernummers, factuurnummers of mededelingen in alle documenten.</p>
      </div>

      {/* Zoekbalk */}
      <div className="relative mb-5" style={{ maxWidth: 700 }}>
        <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#002FA7] pointer-events-none" weight="bold" />
        <input ref={inputRef} type="search" value={q} onChange={e => setQ(e.target.value)}
          className="field-input w-full text-base py-3"
          style={{ paddingLeft: 44, paddingRight: q ? 44 : 16 }}
          placeholder="Bv. KRUIS001, +++123/4567/89012+++, FAC-2026, werkbon-nummer..."
          data-testid="zoek-ref-input" />
        {q && (
          <button className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500" onClick={() => setQ('')} data-testid="zoek-ref-clear">
            <XCircle size={16} weight="fill" />
          </button>
        )}
      </div>

      {/* Status line */}
      {loading && <div className="text-sm text-gray-400 mb-3">Zoeken...</div>}
      {!loading && searched && (
        <div className="text-sm text-gray-500 mb-3" data-testid="zoek-ref-count">
          <span className="font-bold text-gray-900">{totalResults}</span> resultaten gevonden voor <span className="font-bold text-[#002FA7] font-mono">{q}</span>
          {' — '}
          <span className="text-[#002FA7] font-semibold">{results.uitgaand.length}</span> uitgaand, {' '}
          <span className="text-[#D97706] font-semibold">{results.inkomend.length}</span> inkomend
        </div>
      )}
      {!loading && !searched && q.length > 0 && q.length < 2 && <div className="text-sm text-gray-400 mb-3">Typ minstens 2 karakters...</div>}
      {!loading && searched && totalResults === 0 && (
        <div className="section-card p-8 text-center" data-testid="no-results">
          <div className="text-gray-400 text-sm">Geen documenten gevonden met referentie <span className="font-mono font-bold">{q}</span></div>
          <div className="text-[11px] text-gray-400 mt-1">Probeer een andere zoekterm of controleer op spelfouten.</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* UITGAANDE */}
        {results.uitgaand.length > 0 && (
          <div className="section-card" data-testid="results-uitgaand">
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
              <Receipt size={16} weight="bold" className="text-[#002FA7]" />
              <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Uitgaande facturen</span>
              <span className="ml-auto text-[10px] font-bold bg-[#002FA7] text-white px-2 py-0.5">{results.uitgaand.length}</span>
            </div>
            <div className="divide-y divide-gray-100">
              {results.uitgaand.map(d => (
                <Link key={d.id} to={`/documenten/${d.id}/bewerk`} className="block p-3 hover:bg-[#F0F4FF] transition-colors group" data-testid={`result-uit-${d.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 bg-gray-100 text-gray-600">{d.type}</span>
                      <span className="font-mono font-bold text-sm text-gray-900">{highlight(d.id)}</span>
                      {d.status === 'betaald' && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-200">Betaald</span>}
                      {d.status === 'vervallen' && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200">Vervallen</span>}
                    </div>
                    <ArrowRight size={14} className="text-gray-300 group-hover:text-[#002FA7] transition-colors" />
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="font-medium text-gray-900">{d.klant}</span>
                    <span>•</span>
                    <span>{fmtDate(d.datum)}</span>
                    <span>•</span>
                    <span className="font-mono font-bold text-gray-700">{fmtEur(d.totaal)}</span>
                  </div>
                  {d.matching_lijnen && d.matching_lijnen.length > 0 && (
                    <div className="mt-2 pl-3 border-l-2 border-[#002FA7]/30 space-y-0.5">
                      {d.matching_lijnen.map((l, i) => (
                        <div key={i} className="text-[11px] text-gray-600 flex items-center gap-2">
                          <span className="font-mono font-bold text-[#002FA7]">{highlight(l.referentie)}</span>
                          <span className="text-gray-400">—</span>
                          <span className="truncate">{l.omschr}</span>
                          <span className="ml-auto text-gray-400 whitespace-nowrap">{l.aantal} × {fmtEur(l.prijs)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* INKOMENDE */}
        {results.inkomend.length > 0 && (
          <div className="section-card" data-testid="results-inkomend">
            <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
              <Tray size={16} weight="bold" className="text-[#D97706]" />
              <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Inkomende documenten</span>
              <span className="ml-auto text-[10px] font-bold bg-[#D97706] text-white px-2 py-0.5">{results.inkomend.length}</span>
            </div>
            <div className="divide-y divide-gray-100">
              {results.inkomend.map(d => (
                <Link key={d.id} to={`/inkomende-documenten/${d.id}`} className="block p-3 hover:bg-amber-50/40 transition-colors group" data-testid={`result-ink-${d.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-sm text-gray-900">{highlight(d.factuurnummer || d.id)}</span>
                      {d.status_doc === 'betaald' && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-200">Betaald</span>}
                    </div>
                    <ArrowRight size={14} className="text-gray-300 group-hover:text-[#D97706] transition-colors" />
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                    <span className="font-medium text-gray-900">{highlight(d.leverancier)}</span>
                    <span>•</span>
                    <span>{fmtDate(d.datum)}</span>
                    <span>•</span>
                    <span className="font-mono font-bold text-gray-700">{fmtEur(d.totaal)}</span>
                    {d.categorie && <><span>•</span><span className="inline-flex items-center gap-1 text-[10px] text-gray-500"><Tag size={9} />{d.categorie}</span></>}
                  </div>
                  {d.matched_fields && d.matched_fields.length > 0 && (
                    <div className="mt-2 pl-3 border-l-2 border-[#D97706]/30 space-y-0.5">
                      {d.matched_fields.map((m, i) => (
                        <div key={i} className="text-[11px] text-gray-600 flex items-center gap-2">
                          <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider w-20">{m.veld.replace('_', ' ')}</span>
                          <span className="truncate font-mono">{highlight(m.waarde)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
